import axios from "axios";
import reportTranslate from "@/mixins/dictionaries/reportTranslate";
import { NOT_AVAILABLE_MESSAGE } from "@/store/prohor/prohorLine1";

const formatReportType = (type) =>
    reportTranslate[type] || type || "Тест";

const formatTestLines = (testsData) => {
    if (!testsData?.total_counts) return [];

    const lines = [];
    const total = testsData.total_counts.total;
    if (total) {
        lines.push(
            `Тесты (всего): выдано ${total.issued_users_count ?? 0}, ` +
                `свободно ${total.free_count ?? 0}, завершено ${total.finished_count ?? 0}` +
                (total.closest_expiring_date
                    ? `, ближайшее истечение: ${total.closest_expiring_date}`
                    : "") +
                (total.remaining_active_days != null
                    ? `, осталось дней активности: ${total.remaining_active_days}`
                    : "")
        );
    }

    Object.entries(testsData.total_counts).forEach(([key, value]) => {
        if (key === "total" || !value || typeof value !== "object") return;
        lines.push(
            `  • ${formatReportType(key)}: свободно ${value.free_count ?? 0}, ` +
                `выдано ${value.issued_users_count ?? 0}, завершено ${value.finished_count ?? 0}`
        );
    });

    return lines;
};

const formatCourseLines = (coursesData) => {
    if (!coursesData?.total_counts) return [];
    const lines = [];
    const total = coursesData.total_counts.total;
    if (total) {
        lines.push(
            `Курсы (всего): свободно ${total.free_count ?? 0}, ` +
                `выдано ${total.issued_users_count ?? 0}, завершено ${total.finished_count ?? 0}`
        );
    }
    return lines;
};

const formatSubscriptionLines = (subscriptions) => {
    if (!Array.isArray(subscriptions) || !subscriptions.length) {
        return ["Активные подписки: нет данных или не подключены."];
    }
    return subscriptions.map(
        (s) =>
            `Подписка «${s.title || s.product || "—"}»: до ${s.expired_at || "—"}` +
            (s.active_days_remaining != null
                ? `, осталось дней: ${s.active_days_remaining}`
                : "")
    );
};

export const isInventoryQuestion = (question) => {
    const n = question
        .toLowerCase()
        .replace(/ё/g, "е")
        .replace(/[!?.,;:]/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const patterns = [
        /что у меня есть/,
        /что есть у меня/,
        /что есть в наличии/,
        /сколько.*тест/,
        /сколько.*отчет/,
        /сколько.*квот/,
        /сколько.*доступ/,
        /сколько.*курс/,
        /сколько.*тренажер/,
        /сколько.*подписк/,
        /осталось.*тест/,
        /осталось.*квот/,
        /осталось.*доступ/,
        /сколько.*остал/,
        /когда.*сгор/,
        /когда.*истек/,
        /мои доступ/,
        /мои квот/,
        /баланс.*компани/,
        /свободн.*квот/,
        /неиспользован/,
        /что.*назначен/,
        /мои назначен/,
    ];

    return patterns.some((p) => p.test(n));
};

export const fetchCompanyInventory = async () => {
    const { data } = await axios.get("/dashboard/company", { notifyShow: false });
    return data;
};

export const fetchPersonalInventory = async () => {
    const { data } = await axios.get("/dashboard/personal", { notifyShow: false });
    return data;
};

export const buildCompanyInventoryText = (data) => {
    if (!data) {
        return "Не удалось загрузить сводку по компании. Откройте «Моя компания» или попробуйте позже.";
    }

    const parts = ["Вот что сейчас есть по вашей компании:\n"];
    const testLines = formatTestLines(data.tests);
    const courseLines = formatCourseLines(data.courses);

    if (testLines.length) {
        parts.push("Оценка (тесты):");
        parts.push(...testLines);
    } else {
        parts.push("Оценка (тесты): данных нет — возможно, квоты ещё не закуплены.");
    }

    if (courseLines.length) {
        parts.push("\nРазвитие (курсы):");
        parts.push(...courseLines);
    }

    if (data.subscription) {
        parts.push("\nПодписки и инструменты:");
        parts.push(...formatSubscriptionLines(data.subscription));
    }

    parts.push(
        "\nПодробная таблица с датами истечения — в разделе «Моя компания». " +
            "Выдача и статусы прохождений — в «Оценке»."
    );

    return parts.join("\n");
};

export const buildPersonalInventoryText = (data) => {
    if (!data) {
        return "Не удалось загрузить ваши назначения. Откройте «Моё развитие» или попробуйте позже.";
    }

    const parts = ["Вот что назначено вам:\n"];
    const passings = data.passings || [];
    const courses = data.courses || [];

    if (passings.length) {
        parts.push("Тестирования:");
        passings.forEach((p) => {
            parts.push(
                `  • ${p.title || formatReportType(p.report_type)} — статус: ${p.status || "—"}` +
                    (p.expires_at ? `, до ${p.expires_at}` : "")
            );
        });
    } else {
        parts.push("Тестирования: пока не назначены. Обратитесь к HR, если ожидаете тест.");
    }

    if (courses.length) {
        parts.push("\nКурсы и тренажёры:");
        courses.forEach((c) => {
            parts.push(
                `  • ${c.title || "Курс"} — прогресс ${c.progress ?? 0}%` +
                    (c.expires_at ? `, доступ до ${c.expires_at}` : "")
            );
        });
    } else {
        parts.push("\nКурсы и тренажёры: пока не назначены.");
    }

    parts.push("\nВсе карточки — в разделе «Моё развитие».");

    return parts.join("\n");
};

export const buildInventoryReply = async (role) => {
    const key = ["superadmin", "admin", "hr_department"].includes(role)
        ? "hr"
        : "staff";

    if (key === "hr") {
        try {
            const data = await fetchCompanyInventory();
            return {
                text: buildCompanyInventoryText(data),
                actions: [
                    { label: "Моя компания", route: "/" },
                    { label: "Оценка", route: "/rating" },
                ],
            };
        } catch (e) {
            return {
                text:
                    "Сейчас не получилось загрузить квоты компании. " +
                    "Откройте «Моя компания» — там таблица доступов с остатками и сроками.",
                actions: [
                    { label: "Моя компания", route: "/" },
                    { label: "Оценка", route: "/rating" },
                ],
            };
        }
    }

    if (key === "staff") {
        try {
            const data = await fetchPersonalInventory();
            return {
                text: buildPersonalInventoryText(data),
                action: { label: "Моё развитие", route: "/my-development" },
            };
        } catch (e) {
            return {
                text:
                    "Сейчас не получилось загрузить ваши назначения. " +
                    "Посмотрите их в разделе «Моё развитие».",
                action: { label: "Моё развитие", route: "/my-development" },
            };
        }
    }

    return { text: NOT_AVAILABLE_MESSAGE, action: null };
};
